import {
  createContext,
  useContext,
  type FC,
  type ReactNode,
  useMemo,
} from "react";

import {
  type BaseFormkitContextType,
  type ColumnOption,
  type FormkitDataContextType,
  useFormkitContext,
  useFormkitDataContext,
} from "src/formkit/components/formkit-context";

type Props = {
  children?: ReactNode;
  errors: any;
  setErrors: (error: any) => void;
  config: any;
  setConfig: (config: any) => void;
  setCustomValidation: (object: {
    validate: (config: any) => Promise<{ yupError?: any; otherError?: any }>;
  }) => void;
};

export type DestinationFormContextType = Props &
  BaseFormkitContextType &
  FormkitDataContextType & {
    hightouchColumns: ColumnOption[];
  };

export const DestinationFormContext = createContext<DestinationFormContextType>(
  {} as DestinationFormContextType,
);
DestinationFormContext.displayName = "DestinationFormContext";

export const useDestinationForm = () => useContext(DestinationFormContext);

export const DestinationFormProvider: FC<Readonly<Props>> = ({
  children,
  config,
  errors,
  setConfig,
  setCustomValidation,
  setErrors,
}) => {
  const {
    columns,
    destination,
    destinationDefinition,
    hasDependentSyncRun,
    matchBoosterRegionControl,
    model,
    selectedMatchBoosterRegion,
    slug,
    sourceDefinition,
    supportsMatchboosting,
    sync,
    syncTemplate,
    validate,
    isModelDraft,
    draftChanges,
    isEventForwardingForm,
  } = useFormkitContext();
  const { loadingModel, loadingRows, reloadRows, queryRowsError, rows } =
    useFormkitDataContext();

  const value = useMemo(
    () => ({
      config,
      destination,
      destinationDefinition,
      errors,
      hightouchColumns: columns,
      supportsMatchboosting,
      hasDependentSyncRun,
      loadingModel,
      loadingRows,
      matchBoosterRegionControl,
      model,
      reloadRows,
      queryRowsError,
      rows,
      selectedMatchBoosterRegion,
      setConfig,
      setCustomValidation,
      setErrors,
      slug,
      sourceDefinition,
      sync,
      syncTemplate,
      validate,
      isModelDraft,
      draftChanges,
      isEventForwardingForm,
    }),
    [
      config,
      destination,
      destinationDefinition,
      errors,
      columns,
      supportsMatchboosting,
      hasDependentSyncRun,
      loadingModel,
      loadingRows,
      matchBoosterRegionControl,
      model,
      reloadRows,
      queryRowsError,
      rows,
      selectedMatchBoosterRegion,
      setConfig,
      setCustomValidation,
      setErrors,
      slug,
      sourceDefinition,
      sync,
      syncTemplate,
      validate,
      isModelDraft,
      draftChanges,
      isEventForwardingForm,
    ],
  );

  return (
    <DestinationFormContext.Provider value={value}>
      {children}
    </DestinationFormContext.Provider>
  );
};
